import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * How long a link request may take before the control gives up and says so.
 *
 * Every request needs a limit (TECHNICAL_DEBT §0bn: a dead connection must not
 * read as endless work). These are single indexed reads and writes on one
 * `ShareLink` row — well under a second — so fifteen seconds only ever fires
 * on a connection that is not coming back.
 */
export const AGENT_MCP_LINK_TIMEOUT_MS = 15_000;

/** A workspace's private Claude connector link (AGENT_EXPORT_MCP.md §4.2). */
export interface AgentMcpLink {
  token: string;
  domain: string;
  expiresAt: string | null;
  createdAt: string;
  /** The URL to paste into Claude's "Add custom connector". */
  connectorUrl: string;
}

interface ShareRow {
  token: string;
  resourceId: string;
  expiresAt: string | null;
  createdAt: string;
  /** Set by the server when it knows its public address (the ngrok tunnel locally). */
  connectorUrl?: string | null;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

/**
 * The connector link is a `ShareLink` of type `agent-mcp`, so it rides the
 * existing share routes: one live link per workspace and domain, revocable,
 * always expiring. The MCP endpoint it opens is `/api/v1/mcp/<token>`.
 */
@Injectable({ providedIn: 'root' })
export class AgentMcpLinkService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  private toLink(row: ShareRow): AgentMcpLink {
    return {
      token: row.token,
      domain: row.resourceId,
      expiresAt: row.expiresAt,
      createdAt: row.createdAt,
      // Absolute whatever `apiUrl` is: Claude calls it from outside, so a
      // relative path would be a link to nowhere.
      connectorUrl: row.connectorUrl || new URL(`${this.API_URL}/mcp/${row.token}`, window.location.origin).href
    };
  }

  /** The domain's live link, or null when there is none. */
  get(domain: string): Observable<AgentMcpLink | null> {
    return this.http
      .get<Envelope<{ shares: ShareRow[] }>>(`${this.API_URL}/share`, {
        params: { resourceType: 'agent-mcp', resourceId: domain }
      })
      .pipe(
        timeout(AGENT_MCP_LINK_TIMEOUT_MS),
        map((res) => (res.data.shares.length ? this.toLink(res.data.shares[0]) : null))
      );
  }

  /** Creates the domain's link, or returns the live one if it already has one. */
  create(domain: string): Observable<AgentMcpLink> {
    return this.http
      .post<Envelope<ShareRow>>(`${this.API_URL}/share`, { resourceType: 'agent-mcp', resourceId: domain })
      .pipe(
        timeout(AGENT_MCP_LINK_TIMEOUT_MS),
        map((res) => this.toLink(res.data))
      );
  }

  revoke(token: string): Observable<void> {
    return this.http.delete<unknown>(`${this.API_URL}/share/${token}`).pipe(
      timeout(AGENT_MCP_LINK_TIMEOUT_MS),
      map(() => undefined)
    );
  }
}
